/**
 * ISTITUZIONALE O NO — la domanda si fa sull'HOST, e la risposta finisce sotto gli occhi del lettore.
 *
 * Serve perché il worker deduceva la classe della fonte per ESCLUSIONE: prendeva gli host dei fatti
 * non marcati `secondaria` e li chiamava istituzionali. Ma nel ledger del microservizio `secondaria`
 * marca **solo** i fatti `PRATICO` di host non pubblico: un fatto `STORIA`, `MISURA`, `VOCE` o
 * `STATO` resta `primaria` qualunque sia l'host. Conseguenza misurata sul post 45: bastava che un
 * portale turistico commerciale fornisse un fatto storico perché il suo dominio comparisse in pagina
 * col badge «fonte istituzionale», e `provenance.sourcesInstitutional` valeva 4 su 3 fonti mostrate.
 *
 * DUPLICATO INTENZIONALE di isInstitutionalHost in os-service-article-generator/src/ledger.js: due
 * repo, nessun pacchetto condiviso. Con UNA differenza deliberata, che è anche una correzione di
 * quella copia: la label si cerca in QUALSIASI posizione del dominio, non solo nella prima.
 * Misurato là: `mobilita.provincia.bz.it`, `news.provincia.bz.it`, `servizi.comune.roma.it` e
 * `turismo.regione.veneto.it` risultavano tutti `secondaria` — cioè i domini con cui gli enti
 * italiani pubblicano davvero, che sono sottodomini tematici.
 *
 * Non esiste il giudizio inverso ("commerciale"): classificare per sottostringa sbaglia in modo
 * sistematico (`touringclub.it` non è commerciale perché contiene "tour"). Qui c'è una sola domanda
 * con risposta verificabile — l'host appartiene a un ente pubblico? — e tutto il resto è
 * `secondaria`, che non è un'accusa: è l'obbligo di scrivere chi lo dice.
 */

/** Enti nazionali e sovranazionali il cui dominio non ha un suffisso che li identifichi. */
const INSTITUTIONAL_HOSTS = new Set([
  'istat.it', 'beniculturali.it', 'cultura.gov.it', 'salute.gov.it', 'protezionecivile.it',
  'isprambiente.it', 'inps.it', 'agenziaentrate.gov.it', 'camera.it', 'senato.it', 'quirinale.it',
  'governo.it', 'normattiva.it', 'gazzettaufficiale.it', 'cnr.it', 'enea.it', 'ingv.it', 'iss.it',
  'parks.it', 'unesco.org', 'who.int', 'europarl.europa.eu',
])

/**
 * Label che identificano un ente pubblico italiano, cercate in qualsiasi posizione del dominio.
 * `provinz` e `landesregierung` sono la forma tedesca dell'Alto Adige, dove l'ente pubblica su
 * entrambe (`provincia.bz.it` e `provinz.bz.it`).
 */
const PUBLIC_LABELS = /(?:^|\.)(?:comune|comuni|provincia|provinz|regione|cittametropolitana|landesregierung|prefettura|questura)(?:\.|$)/

export const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./i, '').toLowerCase()
  } catch {
    return ''
  }
}

export function isInstitutionalHost(host?: string | null): boolean {
  const h = String(host ?? '').replace(/^www\./i, '').toLowerCase()
  if (!h) return false
  if (INSTITUTIONAL_HOSTS.has(h)) return true
  return (
    /\.(?:gov|edu)\.it$/.test(h) ||
    /\.(?:gov|edu|mil|int)$/.test(h) ||
    /\.europa\.eu$/.test(h) ||
    /(?:^|\.)arpa[a-z]{0,3}\.it$/.test(h) ||
    PUBLIC_LABELS.test(h)
  )
}

/** La classe che va in pagina accanto alla fonte. */
export const trustClassOf = (host?: string | null): 'istituzionale' | 'secondaria' =>
  isInstitutionalHost(host) ? 'istituzionale' : 'secondaria'
